import { useEffect, useRef } from "react";
import useAuth from "@/hooks/useAuth";
import useTabStore from "@/stores/useTabStore";
import { usePortfoliosCRUD } from "@/pages/PortfolioBuilder/api/usePortfoliosCRUD";
import { getPrebuiltOverrides } from "@/pages/PortfolioBuilder/prebuiltWatchlists";
import { highlightedKeys, seedOrder } from "@/shared/utils/highlightOrder";

/**
 * Gives every existing widget the highlight order its lists' priorities used
 * to produce — once, the first time the user's layout loads after priority
 * moved from the lists to the widgets.
 *
 * Without it, a widget highlighting two lists that overlap would switch to the
 * order they were ticked, and some headlines would change colour on the day
 * this shipped for no reason the user could see. See seedOrder for the rule.
 *
 * Only widgets with two or more highlighted lists and no saved order are
 * touched, in one write, so one save covers it and the saved layout carries it
 * to the user's other devices. A widget that has an order is never reseeded.
 *
 * Waits for the layout from the server — never a cached copy after a failed
 * read, which is display-only and must not be written over the real one — and
 * for the user's lists, whose stored priority it reads. The server keeps that
 * priority and the browser keeps its old Newsquawk-list overrides, so a user
 * who comes back months later still gets their order. Safe to delete once
 * everyone has loaded a layout since.
 */
export function useSeedHighlightOrders(): void {
  const { authenticated } = useAuth();
  const { portfoliosQuery } = usePortfoliosCRUD();
  const layoutReady = useTabStore(
    (state) => state.tabsLoaded && !state.tabsLoadFailed && !state.tabsFromCache
  );
  const seeded = useRef(false);

  useEffect(() => {
    if (seeded.current || !authenticated || !layoutReady) return;
    if (!portfoliosQuery.isSuccess) return;
    seeded.current = true;

    const newsquawkPriorities: Record<number, number | undefined> = {};
    for (const [id, override] of Object.entries(getPrebuiltOverrides())) {
      newsquawkPriorities[Number(id)] = override.priority;
    }

    const orders: { tabId: string; widgetId: string; order: string[] }[] = [];
    for (const tab of useTabStore.getState().tabs) {
      for (const [widgetId, data] of Object.entries(tab.widgetFilters ?? {})) {
        if (data.highlightOrder) continue;
        if (highlightedKeys(data.highlight).length < 2) continue;
        orders.push({
          tabId: tab.id,
          widgetId,
          order: seedOrder(data.highlight, portfoliosQuery.data ?? [], newsquawkPriorities),
        });
      }
    }
    if (orders.length > 0) useTabStore.getState().seedHighlightOrders(orders);
  }, [authenticated, layoutReady, portfoliosQuery.isSuccess, portfoliosQuery.data]);
}
